/**
 * LintPanel
 *
 * Collapsible panel showing categorized lint issues.
 * Can be placed below the form builder or in a sidebar.
 */

import { useCallback, useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  alpha,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import type {
  LintIssue,
  LintResult,
  LintSeverity,
} from "@/components/online-reporting/form-management/linter/types";

export interface LintPanelProps {
  result: LintResult | null;
  isLinting?: boolean;
  onIssueClick?: (issue: LintIssue) => void;
  defaultExpanded?: boolean;
  maxHeight?: number | string;
}

const SEVERITY_CONFIG: Record<
  LintSeverity,
  { icon: React.ReactNode; color: string; label: string }
> = {
  error: {
    icon: <ErrorOutlineIcon fontSize="small" />,
    color: "error.main",
    label: "Error",
  },
  warning: {
    icon: <WarningAmberIcon fontSize="small" />,
    color: "warning.main",
    label: "Warning",
  },
  info: {
    icon: <InfoOutlinedIcon fontSize="small" />,
    color: "info.main",
    label: "Info",
  },
};

export function LintPanel({
  result,
  isLinting = false,
  onIssueClick,
  defaultExpanded = true,
  maxHeight = 300,
}: LintPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  // Sync expanded state when defaultExpanded prop changes (e.g., from LintSummary toggle)
  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const toggleRule = useCallback((ruleId: string) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  }, []);

  if (isLinting) {
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
        <Typography variant="body2" color="text.secondary">
          Running lint checks...
        </Typography>
      </Paper>
    );
  }

  if (!result) {
    return null;
  }

  const { issues, errors, warnings, infos, isValid } = result;

  // Group issues by rule
  const issuesByRule = new Map<string, LintIssue[]>();
  issues.forEach((issue) => {
    const existing = issuesByRule.get(issue.rule) || [];
    existing.push(issue);
    issuesByRule.set(issue.rule, existing);
  });

  const totalIssues = issues.length;
  const hasIssues = totalIssues > 0;
  const isClean = isValid && !hasIssues;

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          bgcolor: (theme) =>
            isClean
              ? alpha(theme.palette.success.main, 0.12)
              : errors > 0
                ? alpha(theme.palette.error.main, 0.12)
                : alpha(theme.palette.warning.main, 0.12),
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isClean ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : errors > 0 ? (
            <ErrorOutlineIcon color="error" fontSize="small" />
          ) : (
            <WarningAmberIcon color="warning" fontSize="small" />
          )}
          <Typography variant="subtitle2">
            {isClean
              ? "All checks passed"
              : `${totalIssues} issue${totalIssues !== 1 ? "s" : ""} found`}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {errors > 0 && (
            <Chip
              size="small"
              label={`${errors} error${errors !== 1 ? "s" : ""}`}
              color="error"
              variant="filled"
            />
          )}
          {warnings > 0 && (
            <Chip
              size="small"
              label={`${warnings} warning${warnings !== 1 ? "s" : ""}`}
              color="warning"
              variant="filled"
            />
          )}
          {infos > 0 && (
            <Chip
              size="small"
              label={`${infos} info${infos !== 1 ? "s" : ""}`}
              color="info"
              variant="outlined"
            />
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Box>

      {/* Issues List */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ maxHeight, overflowY: "auto" }}>
          {isClean ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No issues found. Your survey configuration is valid.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {Array.from(issuesByRule.entries()).map(
                ([ruleId, ruleIssues]) => (
                  <Box key={ruleId}>
                    {/* Rule Header */}
                    <ListItem
                      sx={{
                        bgcolor: "grey.50",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "grey.100" },
                      }}
                      onClick={() => toggleRule(ruleId)}
                    >
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {ruleId}
                            </Typography>
                            <Chip
                              size="small"
                              label={ruleIssues.length}
                              sx={{ height: 18, fontSize: "0.7rem" }}
                            />
                          </Stack>
                        }
                      />
                      <IconButton size="small">
                        {expandedRules.has(ruleId) ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </ListItem>

                    {/* Rule Issues */}
                    <Collapse in={expandedRules.has(ruleId)}>
                      <List dense disablePadding sx={{ pl: 2 }}>
                        {ruleIssues.map((issue) => (
                          <IssueItem
                            key={issue.id}
                            issue={issue}
                            onClick={onIssueClick}
                          />
                        ))}
                      </List>
                    </Collapse>
                    <Divider />
                  </Box>
                ),
              )}
            </List>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

interface IssueItemProps {
  issue: LintIssue;
  onClick?: (issue: LintIssue) => void;
}

function IssueItem({ issue, onClick }: IssueItemProps) {
  const config = SEVERITY_CONFIG[issue.severity];

  return (
    <ListItem
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { bgcolor: "action.hover" } : {},
      }}
      onClick={() => onClick?.(issue)}
    >
      <ListItemIcon sx={{ minWidth: 32, color: config.color }}>
        {config.icon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" component="span">
            {issue.questionName && (
              <Typography
                component="span"
                variant="body2"
                fontWeight="medium"
                sx={{ mr: 1 }}
              >
                {issue.questionName}:
              </Typography>
            )}
            {issue.message}
          </Typography>
        }
      />
    </ListItem>
  );
}

/**
 * Compact inline version for use in form builder header.
 */
export function LintSummary({
  result,
  onClick,
}: {
  result: LintResult | null;
  onClick?: () => void;
}) {
  if (!result) return null;

  const { errors, warnings, infos, isValid } = result;

  // Show "Valid" only if no issues at all
  if (isValid && warnings === 0 && infos === 0) {
    return (
      <Tooltip title="All lint checks passed">
        <Chip
          size="small"
          icon={<CheckCircleIcon />}
          label="Valid"
          color="success"
          variant="outlined"
          onClick={onClick}
          sx={{ cursor: onClick ? "pointer" : "default" }}
        />
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" spacing={0.5}>
      {errors > 0 && (
        <Tooltip title={`${errors} error${errors !== 1 ? "s" : ""}`}>
          <Chip
            size="small"
            icon={<ErrorOutlineIcon />}
            label={errors}
            color="error"
            onClick={onClick}
            sx={{ cursor: onClick ? "pointer" : "default" }}
          />
        </Tooltip>
      )}
      {warnings > 0 && (
        <Tooltip title={`${warnings} warning${warnings !== 1 ? "s" : ""}`}>
          <Chip
            size="small"
            icon={<WarningAmberIcon />}
            label={warnings}
            color="warning"
            onClick={onClick}
            sx={{ cursor: onClick ? "pointer" : "default" }}
          />
        </Tooltip>
      )}
      {infos > 0 && (
        <Tooltip title={`${infos} info${infos !== 1 ? "s" : ""}`}>
          <Chip
            size="small"
            icon={<InfoOutlinedIcon />}
            label={infos}
            color="info"
            variant="outlined"
            onClick={onClick}
            sx={{ cursor: onClick ? "pointer" : "default" }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}
